// Sentry web SDK 初期化 (Phase 8 Task 8-6).
//
// (architecture.md §21 / consult 2026-05-06 確定方針)
//
// 方針:
// - **DSN env 未設定なら init skip** (dev / CI で Sentry 接続を要求しない).
// - **production / dev で挙動分岐しない**: DSN を渡せば両方で送信、渡さなければ
//   両方で off. 環境差は dev では通常 DSN を渡さない運用で吸収.
// - `tracesSampleRate` default 0.1 (= 5k events/月の Sentry 無料枠想定).
// - **test 環境** (`import.meta.env.MODE === 'test'`) では DSN が誤って渡されても
//   常に skip. unit test で本物の Sentry に送らないため.
// - source map upload は `vite.config.ts` 側 (`@sentry/vite-plugin`) の役割で、
//   ここでは扱わない.
//
// 使い方 (`main.tsx`):
//   initSentry({
//     dsn: import.meta.env.VITE_SENTRY_DSN,
//     environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
//     release: import.meta.env.VITE_SENTRY_RELEASE,
//     tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
//   });

import * as Sentry from '@sentry/react';

export interface InitSentryOptions {
  /** Sentry DSN. 未設定 (undefined / 空文字) なら init skip. */
  readonly dsn?: string | undefined;
  /** environment tag. e.g. 'production' / 'staging'. 未設定なら未付与. */
  readonly environment?: string | undefined;
  /** release tag. 通常は git commit hash. 未設定なら未付与. */
  readonly release?: string | undefined;
  /** trace sampling rate (0..1). default 0.1 (= Sentry 無料枠想定). */
  readonly tracesSampleRate?: number | undefined;
  /**
   * Test 環境フラグ. `import.meta.env.MODE === 'test'` を呼び出し側で渡すと
   * 強制 skip. 単体テストから明示的に上書きしたい場合に使う.
   */
  readonly isTestEnv?: boolean | undefined;
}

/** 初期化した場合 true、skip した場合 false. unit test 用に戻り値を返す. */
export const initSentry = (opts: InitSentryOptions): boolean => {
  if (opts.isTestEnv === true) {
    // 明示 skip (test runner 側からの強制 off).
    return false;
  }
  // `isTestEnv` が省略 (undefined) のとき **だけ** Vite-injected MODE を見る.
  // unit test から `isTestEnv: false` を明示的に渡すケースは「テスト用の
  // 安全弁を意図的に off にして init される本流ロジックを検証」したい意図
  // なので、MODE='test' の auto-skip もまとめて off にする.
  if (opts.isTestEnv === undefined) {
    // import.meta.env は Vite 環境にしか存在しないため typeof で防御.
    // biome-ignore lint/suspicious/noExplicitAny: import.meta.env shape は実行時に決まる.
    const mode = (import.meta as any).env?.MODE as string | undefined;
    // `vitest` は MODE='test' で走るため、`isTestEnv` 未指定で運用側から
    // 呼ばれた場合の安全弁としてここで弾く.
    if (mode === 'test') return false;
  }

  const dsn = opts.dsn;
  if (typeof dsn !== 'string' || dsn.length === 0) {
    // DSN 未設定 = SDK init skip. unhandled error は ErrorBoundary fallback で
    // 受け止めるだけで Sentry には流れない (= dev / CI の通常状態).
    return false;
  }

  Sentry.init({
    dsn,
    integrations: [
      // browser fetch / xhr / history を auto-instrument して trace を生成.
      // WS は Sentry 側に直接 instrument がないので、別途 server 側 OTel と
      // のひも付けは MVP スコープ外 (browser fetch の Sentry trace と
      // server OTel trace の連結まで, consult 2026-05-06).
      Sentry.browserTracingIntegration(),
      // Session Replay は重い & 5k events/月の無料枠を圧迫するので MVP では
      // **入れない**. Phase 9 / 10 で必要になったら opt-in する (consult 2026-05-06).
    ],
    tracesSampleRate: opts.tracesSampleRate ?? 0.1,
    // release / environment は undefined を渡すと Sentry SDK 側で「未指定」扱い.
    release: opts.release,
    environment: opts.environment,
  });

  return true;
};

/**
 * `VITE_SENTRY_TRACES_SAMPLE_RATE` のような env 文字列を 0..1 の数値に
 * パース. 不正値 / 未設定なら undefined を返し、`initSentry` で default 0.1 に
 * 倒れる.
 */
export const parseSampleRate = (raw: string | undefined): number | undefined => {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return undefined;
  if (n < 0 || n > 1) return undefined;
  return n;
};
